import ReactDOMServer from 'react-dom/server'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import * as Sentry from '@sentry/react'
import { store } from '@/store/store'
import { saveHTMLasDocx } from '../fetch/post-download-html-docx'

export async function buildDocumentHTML(content: JSX.Element, title: string) {
  const contentHTML = ReactDOMServer.renderToStaticMarkup(content)

  const document = `
  <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
    </head>
    <body>
      ${contentHTML}
    </body>
  </html>
  `

  return document
}

/**
 * Handles an error that occurs during a conversation download.
 *
 * @param errorMessage - The error message to capture to sentry. (optional)
 * @param conversationId - The ID of the conversation that is being downloaded, if available.
 */
export function handleConversationDownloadError(errorMessage?: string, conversationId?: string) {
  if (errorMessage) {
    Sentry.captureException(new Error(errorMessage), {
      extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, conversationId: conversationId ?? 'unknown' },
    })
  }
  store.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: `Error preparing the download.`, durationMs: kToastDurationMediumMs }))
}

export function formatHtmlForDocXTransformation(sourceHTML: string) {
  const highlightRegex = /<span class="highlighted-snippet bg-yellow-200">(.*?)<\/span>/g
  const h1ToBRegex = /<h\d>(.*?)<\/h\d>/g

  let outputHtml = sourceHTML
  try {
    outputHtml = outputHtml.replace(highlightRegex, '<mark>$1</mark>')
    outputHtml = outputHtml.replace(h1ToBRegex, '<b>$1</b>')
  } catch (error) {
    console.error('Error processing html string:', error)
  }
  return outputHtml
}

export async function downloadReferenceAsDocX(sourceHTML: string, fileName: string, onLoading: (loading: boolean) => void) {
  onLoading(true)
  try {
    const outputHtml = formatHtmlForDocXTransformation(sourceHTML)
    await saveHTMLasDocx({ html: outputHtml, fileName })
    store.dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `${fileName} downloaded`, durationMs: kToastDurationShortMs }))
  } catch (error) {
    store.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Reference download failed', durationMs: kToastDurationShortMs }))
    throw error
  } finally {
    onLoading(false)
  }
}
